import React, { useEffect, useState } from "react";
import { Clock, Car, Sparkles, Wallet, Banknote, Building2, CalendarClock, CheckCircle2, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { DriverShell } from "@/components/Shells";
import { Spinner } from "@/components/States";
import Button from "@/components/Button";
import { api } from "@/lib/api";
import { money, fmtDateTime, cn } from "@/lib/format";

const PAYOUT_CLS = {
  PAID: "bg-success/15 text-success",
  PROCESSING: "bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-400",
  PENDING: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  FAILED: "bg-destructive/15 text-destructive",
  CANCELED: "bg-secondary text-muted-foreground",
};

function InfoTile({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex-1 rounded-xl bg-white/12 px-3 py-3" data-testid={testid}>
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-white/70">
        <Icon className="h-3.5 w-3.5" /> {label}
      </p>
      <p className="mt-1 truncate font-num text-base font-bold text-white">{value}</p>
    </div>
  );
}

function PayoutCard() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api("/driver/payouts").then(setP).catch(() => setP(null));

  useEffect(() => {
    load();
    if (typeof window !== "undefined" && window.location.search.includes("stripe=")) {
      // Returned from Stripe-hosted onboarding — re-sync readiness, then tidy the URL.
      api("/drivers/stripe/status").catch(() => {}).finally(() => {
        load();
        window.history.replaceState({}, "", "/driver/earnings");
      });
    }
  }, []);

  if (!p) return null;

  const ready = p.onboarding?.ready;

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const r = await api("/drivers/stripe/onboarding", { method: "POST", body: { origin_url: window.location.origin } });
      window.location.href = r.url;
    } catch (e) {
      toast.error(e?.message || "Couldn't start payout setup");
      setBusy(false);
    }
  };

  return (
    <div className="mb-4">
      {/* Balance + payout account summary */}
      <div className="rounded-2xl border border-border bg-gradient-to-br from-brand-600 to-brand-700 p-5 text-white shadow-brand">
        <p className="flex items-center gap-2 text-sm font-semibold text-white/85"><Wallet className="h-4 w-4" /> Available earnings</p>
        <p className="mt-1 font-num text-4xl font-extrabold" data-testid="payout-available">{money(p.available)}</p>
        <p className="text-xs text-white/70">Sent to your bank automatically · {money(p.lifetime_paid)} paid out so far</p>

        <div className="mt-4 flex gap-2.5">
          <InfoTile icon={CalendarClock} label="Next payout" value={ready ? (p.next_payout_label || "Daily") : "—"} testid="next-payout" />
          <InfoTile icon={Building2} label="Payout account" value={p.bank_last4 ? `•••• ${p.bank_last4}` : "Not set"} testid="payout-account" />
          <InfoTile icon={Banknote} label="Schedule" value={p.schedule || "Daily"} testid="payout-schedule" />
        </div>
      </div>

      {/* Setup required OR ready confirmation */}
      {!ready ? (
        <div className="mt-3 rounded-2xl border border-border bg-card p-4 shadow-soft" data-testid="payout-setup-card">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent text-brand-600"><ShieldCheck className="h-5 w-5" /></span>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-foreground">Payment setup required</p>
              <p className="mt-0.5 text-sm text-muted-foreground">Complete your secure Stripe payout setup to receive driver earnings. Your bank details are collected and verified by Stripe.</p>
              <Button onClick={startOnboarding} loading={busy} data-testid="complete-payout-setup" className="mt-3">
                Complete Payout Setup <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-2 rounded-xl bg-success/10 px-3 py-2.5 text-xs font-semibold text-success" data-testid="payout-ready-note">
          <CheckCircle2 className="h-4 w-4" /> Payouts active — earnings of {money(p.minimum)}+ are sent to your bank each day.
        </div>
      )}

      {/* Payout history */}
      {p.payouts?.length > 0 && (
        <div className="mt-4 rounded-2xl border border-border bg-card p-4 shadow-soft" data-testid="payout-history">
          <p className="mb-3 text-sm font-bold text-foreground">Payout history</p>
          <div className="space-y-2.5">
            {p.payouts.map((w) => (
              <div key={w.id} data-testid={`payout-row-${w.id}`} className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-foreground">{fmtDateTime(w.requested_at)}</p>
                  <p className="text-xs text-muted-foreground">{w.payout_type || "ACH"}{w.failure_reason ? ` · ${w.failure_reason}` : ""}</p>
                </div>
                <span className="font-num font-bold text-foreground">{money(w.amount)}</span>
                <span className={cn("rounded-full px-2.5 py-1 text-xs font-semibold", PAYOUT_CLS[w.status] || "bg-secondary text-muted-foreground")}>
                  {(w.status || "").toLowerCase()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="flex flex-1 flex-col items-center gap-1 rounded-2xl border border-border bg-card py-4 shadow-soft">
      <Icon className="h-5 w-5 text-brand-600" />
      <p className="font-num text-lg font-extrabold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

const PERIODS = [["today", "Day"], ["week", "Week"], ["month", "Month"]];

export default function DriverEarnings() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("week");

  useEffect(() => {
    api("/driver/earnings").then(setData).catch(() => setData({ trips: [], days: [] }));
  }, []);

  if (!data) {
    return <DriverShell><div className="flex justify-center py-20"><Spinner className="h-7 w-7 text-brand-600" /></div></DriverShell>;
  }

  const days = data.days || [];
  const maxDay = Math.max(1, ...days.map((d) => d.amount));
  const trips = data.trips || [];
  const total = period === "today" ? data.today_total : period === "month" ? data.month_total : data.week_total;
  const tripCount = period === "today" ? data.today_trips : period === "month" ? data.month_trips : data.week_trips;
  const periodLabel = period === "today" ? "Today" : period === "month" ? "This month" : "This week";

  return (
    <DriverShell>
      <div className="mx-auto max-w-2xl px-5 py-6">
        <h1 className="mb-4 text-2xl font-extrabold text-foreground">Earnings</h1>

        {/* Balance + instant cash-out (most important — kept at the top) */}
        <PayoutCard />

        {/* Period segment */}
        <div className="mb-4 flex gap-1 rounded-2xl bg-secondary p-1">
          {PERIODS.map(([k, label]) => (
            <button key={k} onClick={() => setPeriod(k)} data-testid={`period-${k}`}
              className={cn("flex-1 rounded-xl py-2 text-sm font-semibold transition-colors",
                period === k ? "bg-card text-brand-600 shadow-soft" : "text-muted-foreground")}>
              {label}
            </button>
          ))}
        </div>

        {/* Week card */}
        <div className="mb-4 rounded-2xl bg-gradient-to-br from-brand-600 to-brand-700 p-6 text-white shadow-brand">
          <p className="text-sm text-white/85">{periodLabel} · {tripCount ?? 0} trips</p>
          <p className="font-num text-4xl font-extrabold">{money(total)}</p>
          <div className="mt-4 flex items-end justify-between gap-2" style={{ height: 96 }}>
            {days.map((d, i) => (
              <div key={i} className="flex flex-1 flex-col items-center gap-1.5">
                <div className="flex w-3.5 flex-1 items-end overflow-hidden rounded-full bg-white/20">
                  <div className="w-full rounded-full bg-white transition-all" style={{ height: `${Math.round((d.amount / maxDay) * 100)}%`, minHeight: 4 }} />
                </div>
                <span className="text-[11px] font-medium text-white/85">{d.label}</span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-center text-xs text-white/70">This week&apos;s daily earnings</p>
        </div>

        {/* Stat row */}
        <div className="mb-4 flex gap-3">
          <Stat icon={Clock} value={`${data.online_hours ?? 0}h`} label="Online" />
          <Stat icon={Car} value={`${data.week_trips ?? 0}`} label="Wk trips" />
          <Stat icon={Sparkles} value={`${data.points ?? 0}`} label="Points" />
        </div>

        {/* Wallet lifetime */}
        <div className="mb-4 rounded-2xl border border-border bg-card p-4 shadow-soft">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-600"><Wallet className="h-5 w-5 text-white" /></span>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Lifetime earnings (net)</p>
              <p className="font-num text-2xl font-extrabold text-foreground">{money(data.lifetime)}</p>
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Your net take-home after platform fees, including 100% of tips.</p>
        </div>

        {/* Fare breakdown */}
        <h2 className="mb-3 mt-6 text-lg font-bold text-foreground">Fare breakdown</h2>
        {trips.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No completed trips yet. Go online to start earning!</p>
        ) : (
          <div className="space-y-3">
            {trips.map((t) => (
              <div key={t.id} data-testid={`earn-${t.id}`} className="rounded-2xl border border-border bg-card p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate font-semibold text-foreground">{t.customer_name || "Rider"}</p>
                  <p className="font-num font-bold text-brand-600">{money(t.total)}</p>
                </div>
                <p className="mb-3 mt-0.5 truncate text-xs text-muted-foreground">{t.pickup} → {t.destination}</p>
                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Trip fare{t.distance_miles ? ` · ${t.distance_miles} mi` : ""}</span>
                    <span className="font-medium text-foreground">{money(t.fare)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Platform fee</span>
                    <span className="font-medium text-destructive">−{money(t.commission)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tip (100% yours)</span>
                    <span className={cn("font-medium", t.tip > 0 ? "text-success" : "text-foreground")}>{money(t.tip)}</span>
                  </div>
                  <div className="flex justify-between border-t border-border pt-1.5 text-sm">
                    <span className="font-bold text-foreground">You earned (net)</span>
                    <span className="font-num font-bold text-foreground">{money(t.total)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DriverShell>
  );
}
